import { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useLeadContext } from "@/modules/lead-page/context/lead-page.context";
import {
  getLeadReferrals,
  updateLeadReferral,
  deleteLeadReferral,
  getFormFields,
} from "@/modules/lead-page/api/lead-page.v2.api";
import { getDaysFromNow } from "@/modules/lead-page/utils";
import { TOAST_IDS } from "@/modules/common/types";
import { ToastType } from "@/modules/common/types";
import { getErrorMessage, showToast } from "@/modules/common/utils";
import { queryCommonFlags } from "@/api/constants";
import { defaultUserStatuses } from "@/modules/common/constants";
import useUsersFilterOptions from "@/modules/common/hooks/lookups/users-filter-options.hook";

export const useManageReferrals = () => {
  const [openLeadReferralsModal, setOpenLeadReferralsModal] = useState(false);
  const [openAddReferralModal, setOpenAddReferralModal] = useState(false);
  const [referralUser, setReferralUser] = useState<string | null>(null);

  const [editingValidityIdx, setEditingValidityIdx] = useState<number | null>(
    null
  );
  const [validityDraft, setValidityDraft] = useState<number | null>(null);

  const { lead } = useLeadContext();
  const queryClient = useQueryClient();

  // Fetch referrals data
  const { data: referralsData, isLoading: isLoadingReferrals } = useQuery({
    queryKey: ["lead-referrals", lead?.data?.lead.uid],
    queryFn: () => getLeadReferrals(lead?.data?.lead.uid || ""),
    enabled: !!lead?.data?.lead.uid,
    ...queryCommonFlags,
    staleTime: Infinity,
  });

  // Fetch lead type form fields
  const { data: formFields, isLoading: formFieldsLoading } = useQuery({
    queryKey: ["getFormFields"],
    queryFn: () => getFormFields(lead?.data?.lead_type?.form?.uid || ""),
    ...queryCommonFlags,
    enabled: !!lead?.data?.lead_type?.form?.uid,
  });

  const {
    usersFilterOptions,
    usersFilterOptionsLoading,
    usersFilterOptionsError,
    searchTerm: usersSearchTerm,
    setSearchTerm: setUsersSearchTerm,
  } = useUsersFilterOptions({
    initialParams: {
      statuses: defaultUserStatuses,
      organizationUIds: lead?.data?.lead.organization.uid,
    },
    queryKey: "usersFilterOptions",
  });
  // Update validity mutation
  const { mutate: updateValidity, isLoading: isUpdatingValidity } = useMutation(
    {
      mutationFn: ({
        referralUId,
        validityExpirationDate,
      }: {
        referralUId: string;
        validityExpirationDate: string;
      }) => updateLeadReferral(referralUId, validityExpirationDate),
      onSuccess: () => {
        setEditingValidityIdx(null);
        setValidityDraft(null);
        queryClient.invalidateQueries({
          queryKey: ["lead-referrals", lead?.data?.lead.uid],
        });
      },
      onError: (error: {
        response: { data: { message: string; statusCode: number } };
      }) => {
        showToast({
          type: ToastType.ERROR,
          message: getErrorMessage(error),
          toastId: TOAST_IDS.UPDATE_REFERRAL_VALIDITY_ERROR,
          options: {
            position: "bottom-left",
            autoClose: 3000,
          },
        });
      },
    }
  );

  // Delete referral mutation
  const { mutate: deleteReferral, isLoading: isDeletingReferral } = useMutation(
    {
      mutationFn: (referralUId: string) => deleteLeadReferral(referralUId),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["lead-referrals", lead?.data?.lead.uid],
        });
      },
    }
  );

  // Handler functions
  const handleOpenModal = () => setOpenLeadReferralsModal(true);
  const handleCloseModal = () => setOpenLeadReferralsModal(false);

  const handleStartEditing = (idx: number, expirationDate: string | null) => {
    setEditingValidityIdx(idx);
    // Get current days remaining
    const currentDays = getDaysFromNow(expirationDate);

    if (currentDays !== null) {
      // If expired (negative), default to 30 days, otherwise use current days
      setValidityDraft(currentDays < 0 ? 30 : currentDays);
    } else {
      // No expiration date set, default to 30 days
      setValidityDraft(30);
    }
  };

  const handleCancelEditing = () => {
    setEditingValidityIdx(null);
    setValidityDraft(null);
  };

  const handleValidityChange = (value: string) => {
    setValidityDraft(value !== "" ? parseInt(value, 10) : null);
  };

  const handleSaveValidity = (referralUId: string) => {
    if (validityDraft !== null && validityDraft >= 0) {
      // Calculate the new expiration date
      const newExpirationDate = new Date();
      newExpirationDate.setDate(newExpirationDate.getDate() + validityDraft);

      updateValidity({
        referralUId,
        validityExpirationDate: newExpirationDate.toISOString(),
      });
    }
  };

  const handleDeleteReferral = (referralUId: string) => {
    deleteReferral(referralUId);
  };

  return {
    // State
    openLeadReferralsModal,
    openAddReferralModal,
    setOpenAddReferralModal,
    editingValidityIdx,
    validityDraft,

    referralUsers: {
      data: usersFilterOptions,
      loading: usersFilterOptionsLoading,
      error: usersFilterOptionsError,
      search: usersSearchTerm,
      setSearch: setUsersSearchTerm,
      value: referralUser,
      setValue: setReferralUser,
    },

    // Data
    referralsData,
    isLoadingReferrals,
    formFields,
    formFieldsLoading,

    // Loading states
    isUpdatingValidity,
    isDeletingReferral,

    // Handlers
    handleOpenModal,
    handleCloseModal,
    handleStartEditing,
    handleCancelEditing,
    handleValidityChange,
    handleSaveValidity,
    handleDeleteReferral,
  };
};
